import { KeyboardEvent, MouseEvent, ReactNode, TouchEvent, use, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { addDayOffset, DateString } from '../../../../../common/src/date_string.ts';
import {
  BoundSongListEntry,
  CustomMusic,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListType,
  SongSlug,
  UnboundSongListEntry,
} from '../../../../../common/src/model.ts';
import { isBlankEntry, isBound } from '../../../../../common/src/model_util.ts';
import { formatVerses, getVersesSuffix, isSameShownEntry } from '../../../../../common/src/verses.ts';
import SongWarningIcon from '../../../assets/song-warning.svg?react';
import TextWarningIcon from '../../../assets/text-warning.svg?react';
import { HymnalWithHymns } from '../../../data/hymnals.ts';
import { getOperatingSystem } from '../../../util/operating_system.ts';
import { getDisplayNameForSongListEntry } from '../../../util/path.ts';
import { Device, getDevice, getPlatform, Platform } from '../../../util/platform.ts';
import { SearchContext } from '../../../util/search_control.tsx';
import {
  nameBeforeSuffix,
  nameWithSuffixRow,
  suffixAfterNameInFillingRow,
  suffixRoom,
} from '../../../views/name_with_suffix.ts';
import { CellContextMenu } from './cell_context_menu.tsx';
import { CellPosition } from './planner.tsx';
import type { ClearLabel, RepeatingRowBlankMark } from './repeating_row_cells.ts';
import {
  DisplayUnauthorizedAlert,
  isAuthorizedToEditSongList,
  SongCellPadding,
  SongRowHeightDouble,
  SongRowHeightSingle,
  SongRowWidth,
} from './shared.tsx';
import { findSongListEntry, removeSongListEntry, replaceSongListEntry } from './song_list_rows.ts';
import { SongSelector, SongSelectorProps } from './song_selector.tsx';
import { TextCellEditor, TextCellEditorProps } from './text_cell_editor.tsx';
import { AllSongLists } from './use_all_song_lists.ts';
import { VersesPicker } from './verses_picker.tsx';

export const CellHoverOutlineWidth = '3px';

// the keys that clear a cell, or a selected column
export function isClearKey(key: string): boolean {
  return key === 'Backspace' || key === 'Delete';
}

export interface SelectCellOptions {
  isScrolledIntoView?: boolean;
}

// how long a finger rests on a cell before its menu opens, and how far it may drift meanwhile
const longPressDuration = 500;
const longPressTolerance = 10;

// a touch on the cell that may become a long-press: where it started, its timer while pending, and whether it has
// opened the menu
interface LongPress {
  timer?: ReturnType<typeof setTimeout>;
  x: number;
  y: number;
  hasOpenedMenu?: boolean;
}

interface SongCellProps {
  allSongLists: AllSongLists;
  customMusic?: CustomMusic;
  date: DateString;
  hymnals?: HymnalWithHymns[];
  label?: string;
  labelGlyphs?: ReactNode; // shown after the label, e.g. a repeating row's glyphs on a phone (issue #4170)
  tag?: string; // a small pill before the song, e.g. the month of a Same-all-month cell (issue #4170)
  tagOutlined?: boolean; // the pill outlined rather than filled, e.g. a rotating cell not set that Sunday (issue #4171)
  // a By-Sunday-of-the-month cell showing its ordinal's entry without holding it (issue #4171): drawn faded, a song
  // with music in a faded purple and plain text in gray (issue #4202)
  isInherited?: boolean;
  title?: string; // hover text for the cell
  // the Sundays of the cell holding a blank that stops the repeat (issue #4211), each drawn as a faint dash at its own
  // column with its own hover text; only a church's repeating row passes them
  blankMarks?: RepeatingRowBlankMark[];
  // a run of a repeating row (issue #4191): consecutive Sundays showing the same song, drawn as one cell whose Sundays
  // are each selected and edited on their own; the planner draws the selection's reach, so the cell draws no focus
  // border of its own
  isRun?: boolean;
  songList: SongList;
  songListType: SongListType;
  songListEntryType: SongListEntryType;
  // whether the cell's org is a church, whose cells follow the church-only rules: a blank (issue #4190) shows as an
  // empty cell, and an editor closed empty clears only a cell that shows something (issue #4200). A school's or a
  // small group's cell behaves as before those rules, and so does one whose org has not loaded (false)
  isChurch: boolean;
  selectedCell?: CellPosition;
  // isScrolledIntoView false: the planner leaves the grid where it is for this selection
  setSelectedCell?: (cellPosition: CellPosition, options?: SelectCellOptions) => void;
  setIsDialogOpen?: (isDialogOpen: boolean) => void;
  moveDown?: () => void;
  isInHymnOfTheMonthPlanner?: boolean;
  isInternalUser: boolean;
  isOrgAdmin: boolean;
  isStaff?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  row: number;
  columnStart: number;
  columnCount: number;
  onSelectSong?: (songSlug?: SongSlug) => void;
  // when present, edits go here (undefined clears the cell) instead of to songList, with the Sunday edited: in a run
  // with an editor, picker or menu open, the Sunday it opened on (issue #4201); otherwise in a run the selected Sunday
  // when the selection lies within the cell, else the cell's first Sunday (`date`). A repeating row's cell shows a song
  // another Sunday holds, and the caller writes the edited Sunday (issue #4191)
  updateEntry?: (entry: SongListEntry | undefined, date: DateString) => void;
  // the name of the menu's Clear item on the Sunday `date` it is for (the Sunday updateEntry would write), undefined
  // for no Clear item (getClearLabel): when passed, it is the whole answer, a name exactly when Clear there writes
  // something. A repeating row's Sunday holding nothing gets a blank that stops the fill (issue #4200), so a phone,
  // which has no Delete key, can do it too, and the item says which of its three Clears it does (issue #4211); a
  // filled-in Sunday where Clear writes nothing offers no Clear. Without it the menu offers "Clear cell" only on a cell
  // holding an entry, as for a school or a row that does not repeat
  clearLabelAt?: (date: DateString) => ClearLabel | undefined;
  // the cell's context menu offers Copy and Paste only when these are passed (the planner copies and pastes the
  // selected cell, which opening the menu selects)
  onCopyCell?: () => void;
  onPasteCell?: () => void;
}

export const SongCell = ({
  allSongLists,
  customMusic,
  date,
  hymnals,
  label,
  labelGlyphs,
  tag,
  tagOutlined,
  isInherited,
  title,
  blankMarks,
  isRun,
  songList,
  songListType,
  songListEntryType,
  isChurch,
  selectedCell,
  setIsEditing,
  setSelectedCell,
  setIsDialogOpen,
  moveDown,
  isInHymnOfTheMonthPlanner,
  isInternalUser,
  isOrgAdmin,
  isStaff,
  row,
  columnStart,
  columnCount,
  onSelectSong: onSelectSongParent,
  updateEntry,
  clearLabelAt,
  onCopyCell,
  onPasteCell,
}: SongCellProps) => {
  const [songSelectorProps, setSongSelectorProps] = useState<
    Pick<SongSelectorProps, 'selectSearchValue'> | undefined
  >();
  const [textCellEditorProps, setTextCellEditorProps] = useState<
    Pick<TextCellEditorProps, 'selectValue' | 'initialValue'> | undefined
  >();
  const { setSearchString } = use(SearchContext);
  // the stored entry, which Clear and the editors act on, and the one the cell shows: a church's blank (issue #4190)
  // shows as an empty cell, with no warning, no verses and nothing to copy; any other org's entry with empty text is
  // shown as stored, in a Song row with the warning that the row holds text
  const entry = findSongListEntry(songList, row);
  const shownEntry = isChurch && isBlankEntry(entry) ? undefined : entry;
  const editSongCellRef = useRef<HTMLDivElement>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | undefined>();
  const boundSong = isBound(entry) ? { slug: entry.slug, verses: formatVerses(entry.verses) } : undefined;
  // the song and verses the verses picker opened on. A live update that binds the cell to another song, unbinds it or
  // changes its verses closes the picker unsaved: Done would overwrite a choice the picker never showed
  const [versesPickerSong, setVersesPickerSong] = useState<{ slug: SongSlug; verses: string }>();
  if (
    versesPickerSong !== undefined &&
    (versesPickerSong.slug !== boundSong?.slug || versesPickerSong.verses !== boundSong.verses)
  ) {
    setVersesPickerSong(undefined);
  }
  const isVersesPickerOpen = versesPickerSong !== undefined;
  const longPressRef = useRef<LongPress>(undefined);
  const isMenuOrPickerOpen = Boolean(contextMenuPosition) || isVersesPickerOpen;
  // the cell's own editor is open: a right-click or long-press in it is the browser's (Cut, Copy and Paste, the caret)
  const isEditorOpen = Boolean(textCellEditorProps || songSelectorProps);

  const onCloseSongSelector = useCallback(() => {
    setSongSelectorProps(undefined);
  }, []);

  // the open menu is not editing: Ctrl+V with the menu open closes it and pastes into the cell (the planner leaves the
  // paste to an open editor)
  useEffect(() => {
    setIsEditing?.(Boolean(songSelectorProps || textCellEditorProps || isVersesPickerOpen));
    // a cell that unmounts with its editor open (the grid is virtualized) must not leave the
    // planner believing an edit is in progress
    return () => setIsEditing?.(false);
  }, [isVersesPickerOpen, setIsEditing, songSelectorProps, textCellEditorProps]);

  const isDialogOpen = Boolean(songSelectorProps) || isMenuOrPickerOpen;
  useEffect(() => {
    setIsDialogOpen?.(isDialogOpen);
    if (!isDialogOpen) {
      return;
    }
    // the menu or the picker unmounts with the cell: keyboard navigation must not stay switched off
    return () => setIsDialogOpen?.(false);
  }, [isDialogOpen, setIsDialogOpen]);

  // TODO(hewitt): isSelected is false because songList has start date 7/21 (Mon), but selected cell is 7/20 (Sun)
  //               selectedCell & selectedColumn should be based on the column index not a date
  const isSelected =
    selectedCell &&
    row === selectedCell.row &&
    selectedCell.column >= columnStart &&
    selectedCell.column < columnStart + columnCount;
  // the column an edit applies to: the selected one when the selection lies within the cell
  const selectedColumnInCell = isSelected ? selectedCell?.column : undefined;
  const selectionColumn = selectedColumnInCell ?? columnStart;
  // the Sunday of one of the cell's columns: a run's columns are consecutive Sundays from its first; any other cell
  // edits its own Sunday
  const getColumnDate = useCallback(
    (column: number): DateString => (isRun ? addDayOffset(date, (column - columnStart) * 7) : date),
    [columnStart, date, isRun],
  );
  // the Sunday of a run that an open editor, picker or menu edits: the one it opened on, by date, held while it stays
  // open, so neither a selection moving within the run, nor a live update re-laying the row or shifting the planner's
  // columns (issue #4201), moves the write. Every open sets it, and it is read only while something is open.
  const [openedDate, setOpenedDate] = useState<DateString>();
  const isEditOpen = isEditorOpen || isMenuOrPickerOpen;
  const editedDate = (isRun && isEditOpen ? openedDate : undefined) ?? getColumnDate(selectionColumn);

  // an edit of a church's cell to the entry it already holds writes nothing (issue #4210): the selector choosing the
  // song the cell shows, or a text that is the cell's own text. A filled-in Sunday of a repeating row shows an entry
  // another Sunday holds, and a write would make it the edited Sunday's own. Any other org's cell saves its list again,
  // unchanged
  const updateSongListEntry = useCallback(
    (newEntry: SongListEntry) => {
      if (isChurch && entry && isSameShownEntry(newEntry, entry)) {
        return;
      }
      if (updateEntry) {
        updateEntry(newEntry, editedDate);
        return;
      }
      void allSongLists.update(replaceSongListEntry(songList, row, newEntry));
    },
    [allSongLists, editedDate, entry, isChurch, row, songList, updateEntry],
  );

  const deleteSongListEntry = useCallback(() => {
    if (updateEntry) {
      updateEntry(undefined, editedDate);
      return;
    }
    void allSongLists.update(removeSongListEntry(songList, row));
  }, [allSongLists, editedDate, row, songList, updateEntry]);

  // an editor closed empty on a church's cell clears only a cell that shows something: on one showing nothing (holding
  // nothing, or a blank) it changes nothing, where Clear would write a blank in a repeating row or remove the blank
  // (issue #4200). Any other org's cell is cleared as Clear does, which saves the week's list even when it holds
  // nothing for the row
  const clearShownEntry = useCallback(() => {
    if (!isChurch || shownEntry) {
      deleteSongListEntry();
    }
  }, [deleteSongListEntry, isChurch, shownEntry]);

  // on a church's cell, the text editor a keystroke did not open (it opens with its text selected), closed with the
  // text it opened showing, writes nothing (issue #4210), whatever the cell shows: its text, the name of a song bound in
  // a text row, or nothing for a song whose name does not resolve, which an empty close would otherwise clear. An
  // editor a keystroke opened is closed as typed: empty, it clears as below; a text is written unless it is the cell's
  // own (updateSongListEntry). Any other org's cell saves the text, which turns a song in a text row into its name
  const openedSelected = textCellEditorProps?.selectValue;
  const openedText = textCellEditorProps?.initialValue ?? '';
  const onCloseTextCellEditor = useCallback(
    (text: string | undefined) => {
      setTextCellEditorProps(undefined);
      if (text === undefined) {
        return;
      }
      if (isChurch && openedSelected && text === openedText) {
        return;
      }
      if (text === '') {
        clearShownEntry();
        return;
      }
      let newEntry: UnboundSongListEntry;
      if (entry) {
        // strip 'slug' to replace with 'text', and the verses, which only a song has
        const { slug: _, verses: _verses, ...songNoSlug } = entry as BoundSongListEntry;
        newEntry = { ...songNoSlug, text };
      } else {
        newEntry = { row, text };
      }
      updateSongListEntry(newEntry);
    },
    [clearShownEntry, entry, isChurch, openedSelected, openedText, row, updateSongListEntry],
  );

  const bindSongListEntry = useCallback(
    (slug: string) => {
      let newEntry: BoundSongListEntry;
      if (entry) {
        // strip 'text' to replace with 'slug'; the verses were chosen for the old song, so they stay only when
        // the song is the same
        const { text: _, verses: _verses, ...songNoText } = entry as UnboundSongListEntry & { verses?: number[] };
        newEntry = {
          ...songNoText,
          slug,
          ...(isBound(entry) && entry.slug === slug && entry.verses && { verses: entry.verses }),
        };
      } else {
        newEntry = { row, slug };
      }
      updateSongListEntry(newEntry);
    },
    [entry, row, updateSongListEntry],
  );

  const onSelectSong = useCallback(
    (songSlug?: SongSlug) => {
      if (songSlug) {
        bindSongListEntry(songSlug);
      } else {
        clearShownEntry();
      }
      onCloseSongSelector();
      moveDown?.();
      onSelectSongParent?.();
    },
    [bindSongListEntry, clearShownEntry, moveDown, onCloseSongSelector, onSelectSongParent],
  );

  // whether this user may edit the list; when not, says so
  const isAuthorized = useCallback(() => {
    if (isAuthorizedToEditSongList({ isOrgAdmin, isInternalUser, isStaff, songListType })) {
      return true;
    }
    void DisplayUnauthorizedAlert();
    return false;
  }, [isInternalUser, isOrgAdmin, isStaff, songListType]);

  // opens the cell's editor on `column`, the Sunday it then edits in a run: showing the cell's entry, selected, or with
  // `isTyping` (a letter or digit opened it) empty, for the key to type into
  const onEditSongEntry = useCallback(
    (column: number, { isTyping = false }: { isTyping?: boolean } = {}) => {
      if (!isAuthorized()) {
        return;
      }
      setOpenedDate(getColumnDate(column));
      const initialValue = isTyping
        ? undefined
        : getDisplayNameForSongListEntry({ entry: shownEntry, hymnals, customMusic });
      if (songListEntryType !== SongListEntryType.Song) {
        setTextCellEditorProps({ initialValue, selectValue: !isTyping });
      } else {
        if (initialValue) {
          setSearchString(initialValue);
        }
        setSongSelectorProps({ selectSearchValue: !isTyping });
      }
    },
    [customMusic, getColumnDate, hymnals, isAuthorized, setSearchString, shownEntry, songListEntryType],
  );

  // the column a selection made at `clientX` selects: a run's Sunday under the pointer, and with no pointer (a keyboard
  // context menu) the Sunday already selected in the cell; any other cell selects as a whole, at columnStart
  const getColumnAt = useCallback(
    (clientX: number | undefined): number => {
      const cell = editSongCellRef.current;
      if (!isRun || !cell) {
        return columnStart;
      }
      const { left, width } = cell.getBoundingClientRect();
      if (clientX === undefined || width <= 0) {
        return selectedColumnInCell ?? columnStart;
      }
      const offset = Math.floor((clientX - left) / (width / columnCount));
      return columnStart + Math.min(Math.max(offset, 0), columnCount - 1);
    },
    [columnCount, columnStart, isRun, selectedColumnInCell],
  );

  // a click selects the cell, and so does a right-click or long-press, the way spreadsheets do, so the planner's Copy
  // and Paste act on it; in a run, it selects the Sunday under the pointer, `column` (getColumnAt)
  const selectCell = useCallback(
    (column: number, options?: SelectCellOptions) => {
      if (setSelectedCell === undefined) {
        return;
      }
      if (isSelected && (!isRun || selectedColumnInCell === column)) {
        return;
      }
      setSelectedCell({ row, column }, options);
    },
    [isRun, isSelected, row, selectedColumnInCell, setSelectedCell],
  );

  const handleCellMouseDown = useCallback(
    (event: MouseEvent) => {
      // a right-click (or a Mac's control-click; elsewhere Ctrl+click is an ordinary click) opens the context menu
      if (event.button !== 0 || (event.ctrlKey && getOperatingSystem().name === 'macOS')) {
        return;
      }
      // the Sunday under the pointer, read now: the editor opens from a timeout whose closure predates this selection
      const column = getColumnAt(event.clientX);
      // a tap on a phone opens the editor at once, on the run's Sunday it selects
      if (getPlatform() === Platform.Mobile) {
        if (isRun) {
          selectCell(column);
        }
        setTimeout(() => onEditSongEntry(column)); // allows song cell to get focus before the text editor takes focus
        return;
      }
      // Only open the editor if the cell is both selected and active, and in a run only on the selected Sunday: a click
      // on another of its Sundays selects that one.
      // If the user clicks away from the liturgy planner and then clicks back into the selected cell, don't open.
      const isActive = editSongCellRef.current === document.activeElement;
      const isOnSelectedColumn = !isRun || selectedColumnInCell === column;
      if (
        (isSelected && isActive && isOnSelectedColumn) ||
        (songListType === SongListType.HymnOfTheMonth && !isInHymnOfTheMonthPlanner)
      ) {
        setTimeout(() => onEditSongEntry(column));
        return;
      }
      selectCell(column);
    },
    [
      getColumnAt,
      isInHymnOfTheMonthPlanner,
      isRun,
      isSelected,
      onEditSongEntry,
      selectCell,
      selectedColumnInCell,
      songListType,
    ],
  );

  // the menu lists only what this cell can do on the Sunday `menuDate` it is for (issue #4201: in a run, the Sunday under
  // the pointer); with nothing to list it does not open and the browser's menu shows. The one rule for both its items
  // and whether it opens at all. Clear's name says what it does there (issue #4211)
  const getMenuActions = (menuDate: DateString) => {
    const clearLabel = clearLabelAt ? clearLabelAt(menuDate) : entry && 'Clear cell';
    return {
      onChooseVerses:
        songListEntryType === SongListEntryType.Song && isBound(entry)
          ? () => isAuthorized() && setVersesPickerSong(boundSong)
          : undefined,
      onCopy: shownEntry && onCopyCell,
      onPaste: onPasteCell && (() => isAuthorized() && onPasteCell()),
      clear: clearLabel && { label: clearLabel, onSelect: () => isAuthorized() && deleteSongListEntry() },
    };
  };
  const menuActions = getMenuActions(editedDate);
  const hasMenuActionsAt = (column: number) => Object.values(getMenuActions(getColumnDate(column))).some(Boolean);

  // a right-click or long-press opens the menu on the Sunday at `column` and selects it without scrolling it into view:
  // a cell partly out of view would move away from its menu, and the scroll would close the menu as it opens
  const openContextMenu = (x: number, y: number, column: number) => {
    setContextMenuPosition({ x, y });
    // the menu's Clear and the verses picker it opens edit this Sunday
    setOpenedDate(getColumnDate(column));
    selectCell(column, { isScrolledIntoView: false });
  };

  const cancelLongPress = useCallback(() => {
    clearTimeout(longPressRef.current?.timer);
    longPressRef.current = undefined;
  }, []);

  const onContextMenu = (event: MouseEvent) => {
    if (isEditorOpen) {
      return;
    }
    // a run's menu or picker already open keeps its Sunday, and the selection (the planner's Paste target) stays on
    // it rather than moving to another of the run's Sundays (issue #4201), as a cell of one Sunday keeps it
    if (isRun && isMenuOrPickerOpen) {
      event.preventDefault();
      return;
    }
    // Shift+F10 or the Menu key: Chrome fires the contextmenu at the focused cell's centre, Firefox at 0, 0, which
    // is no pointer position: the menu then opens at the cell's bottom left, and a run keeps its selected Sunday
    const isAtNoPointer = event.clientX === 0 && event.clientY === 0;
    // the Sunday the menu is for: the one under the pointer, or the one selected for a keyboard menu
    const column = getColumnAt(isAtNoPointer ? undefined : event.clientX);
    // selected even when the browser's own menu shows: a Ctrl+V that follows pastes into this cell
    selectCell(column, { isScrolledIntoView: false });
    if (!hasMenuActionsAt(column)) {
      return;
    }
    // also Android's own long-press menu, which fires alongside the long-press below: one menu opens, not two
    event.preventDefault();
    const longPress = longPressRef.current;
    if (longPress) {
      clearTimeout(longPress.timer);
      longPressRef.current = { ...longPress, timer: undefined, hasOpenedMenu: true };
    }
    if (isMenuOrPickerOpen) {
      return;
    }
    const cell = editSongCellRef.current;
    if (isAtNoPointer && cell) {
      const { left, bottom } = cell.getBoundingClientRect();
      openContextMenu(left + 8, bottom - 4, column);
      return;
    }
    openContextMenu(event.clientX, event.clientY, column);
  };

  const onTouchStart = (event: TouchEvent) => {
    cancelLongPress();
    if (event.touches.length !== 1 || isMenuOrPickerOpen || isEditorOpen) {
      return;
    }
    const { clientX: x, clientY: y } = event.touches[0];
    const column = getColumnAt(x);
    if (!hasMenuActionsAt(column)) {
      return;
    }
    const timer = setTimeout(() => {
      longPressRef.current = { x, y, hasOpenedMenu: true };
      openContextMenu(x, y, column);
    }, longPressDuration);
    longPressRef.current = { timer, x, y };
  };

  const onTouchMove = useCallback(
    (event: TouchEvent) => {
      const touch = event.touches[0];
      const start = longPressRef.current;
      if (start?.timer && touch && Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > longPressTolerance) {
        cancelLongPress();
      }
    },
    [cancelLongPress],
  );

  const onTouchEnd = useCallback(
    (event: TouchEvent) => {
      const hasOpenedMenu = longPressRef.current?.hasOpenedMenu;
      cancelLongPress();
      // the press that opened the menu ends here: iOS would follow a long-held finger with a click, which would land
      // on the menu's backdrop and close the menu at once. Only this touch's events are cancelled, nothing later.
      if (hasOpenedMenu) {
        event.preventDefault();
      }
    },
    [cancelLongPress],
  );

  useEffect(() => cancelLongPress, [cancelLongPress]);

  // the menu and the picker give the focus back to the cell when they close
  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(undefined);
    editSongCellRef.current?.focus();
  }, []);

  const onVersesPickerCancel = useCallback(() => {
    setVersesPickerSong(undefined);
    editSongCellRef.current?.focus();
  }, []);

  // the verses go to the Sunday the picker opened on, a filled-in one included, as any edit (getRepeatingCellWrites)
  const onVersesChosen = useCallback(
    (verses: number[] | undefined) => {
      onVersesPickerCancel();
      if (!isBound(entry) || formatVerses(verses) === formatVerses(entry.verses)) {
        return;
      }
      const { verses: _, ...entryWithoutVerses } = entry;
      updateSongListEntry({ ...entryWithoutVerses, ...(verses && { verses }) });
    },
    [entry, onVersesPickerCancel, updateSongListEntry],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // also Ctrl+C and Ctrl+V: the song selector and the text editor do not open on a paste; either the text editor
      // is already open and will accept the text, or the planner pastes into the cells
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const keyIsNumber = event.code.match(/^Digit[0-9]$/) || event.code.match(/^Numpad[0-9]$/);
      const keyIsLetter = event.code.match(/^Key[A-Z]$/);
      const isEditingKey = keyIsNumber || keyIsLetter || event.key === 'Enter' || isClearKey(event.key);
      // a held key repeats, and Clear toggles a repeating row's Sunday (issue #4200), so only the press clears; a
      // repeat returns before the authorization check so that holding the key does not raise its alert again and again
      const isRepeatedClear = event.repeat && isClearKey(event.key);
      // only the keys that edit say so to someone who may not: the arrows, Tab and Shift+F10 work for anyone
      if (!isEditingKey || isRepeatedClear || !isAuthorized()) {
        return;
      }

      if (keyIsNumber || keyIsLetter) {
        onEditSongEntry(selectionColumn, { isTyping: true });
        return;
      }

      switch (event.key) {
        case 'Enter':
          onEditSongEntry(selectionColumn);
          break;
        case 'Backspace':
        case 'Delete':
          deleteSongListEntry();
          break;
      }
    },
    [isAuthorized, selectionColumn, onEditSongEntry, deleteSongListEntry],
  );

  // the menu and the picker hold the focus while open: opening the menu selects the cell, and taking the focus back
  // then would send the keys meant for the menu to the cell (a letter opens its editor, Backspace clears it)
  useEffect(() => {
    if (isSelected && !isMenuOrPickerOpen && editSongCellRef.current) {
      editSongCellRef.current.focus();
    }
  }, [isMenuOrPickerOpen, isSelected, songSelectorProps]);

  const isWideFormat = Boolean(columnCount && columnCount > 1);

  const isWarningVisible = Boolean(
    shownEntry &&
    ((songListEntryType === SongListEntryType.Song && !isBound(shownEntry)) ||
      (songListEntryType !== SongListEntryType.Song && isBound(shownEntry))),
  );
  const warning = isWarningVisible && (isBound(shownEntry) ? <TextWarning /> : <SongWarning />);

  const songName = hymnals?.length
    ? getDisplayNameForSongListEntry({ entry: shownEntry, hymnals, customMusic })
    : undefined;
  const versesSuffix = songName ? getVersesSuffix(shownEntry) : undefined;
  const cellSongName = (
    <SongCellSongName $highlight={isBound(entry)} $inherited={!!isInherited}>
      {songName ?? ''}
    </SongCellSongName>
  );

  const gridRow = row;
  const gridColumnStart = (columnStart ?? 0) + 1;
  const gridColumnEnd = gridColumnStart + (columnCount ?? 1);
  return (
    <>
      {contextMenuPosition && <CellContextMenu {...contextMenuPosition} {...menuActions} onClose={closeContextMenu} />}
      {isVersesPickerOpen && isBound(entry) && (
        <VersesPicker
          anchorRef={editSongCellRef}
          songSlug={entry.slug}
          songName={songName ?? entry.slug}
          verses={entry.verses}
          hymnals={hymnals}
          customMusic={customMusic}
          onDone={onVersesChosen}
          onCancel={onVersesPickerCancel}
        />
      )}
      {songSelectorProps && (
        <SongSelector
          key="selector"
          songList={songList}
          onClose={onCloseSongSelector}
          onSongClick={onSelectSong}
          {...songSelectorProps}
        />
      )}
      <EditSongCell
        tabIndex={0}
        key={`${date}-${row}`}
        $isFixedWidth={!isWideFormat}
        $isDoubleHeight={!!label || getPlatform() === Platform.Mobile}
        $drawsFocusBorder={!isRun}
        onMouseDown={handleCellMouseDown}
        onContextMenu={onContextMenu}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={cancelLongPress}
        onKeyDown={onKeyDown}
        ref={editSongCellRef}
        title={title}
        style={{
          gridRow,
          gridColumnStart,
          gridColumnEnd,
          ...(columnCount && { width: `calc(${columnCount} * ${SongRowWidth})` }),
        }}
      >
        {textCellEditorProps ? (
          <TextCellEditor onClose={onCloseTextCellEditor} onEnter={moveDown} {...textCellEditorProps} />
        ) : (
          <SongCellContent $sticky={isWideFormat} $isInternalUser={isInternalUser}>
            {(label || labelGlyphs) && (
              <CellLabel key="header">
                {label}
                {labelGlyphs}
              </CellLabel>
            )}
            <SongCellValue $hasVerses={!!versesSuffix} $hasBlankMarks={Boolean(blankMarks?.length)}>
              {tag && <CellTag $outlined={!!tagOutlined}>{tag}</CellTag>}
              {versesSuffix ? (
                <SongCellSongAndVerses>
                  {cellSongName}
                  <SongCellVerses>{versesSuffix}</SongCellVerses>
                </SongCellSongAndVerses>
              ) : (
                cellSongName
              )}
            </SongCellValue>
          </SongCellContent>
        )}
        {/* not over the open text editor, whose input a mark would cover */}
        {!textCellEditorProps &&
          blankMarks?.map(({ column, title: blankTitle }) => (
            <BlankMark key={column} $column={column} title={blankTitle} data-blank-column={column}>
              {(label || labelGlyphs) && <BlankMarkLabelLine $hasText={Boolean(label)} />}
              <BlankMarkValueLine>
                {tag && <BlankMarkTagSpace $outlined={false} data-tag={tag} />}
                <BlankDash />
              </BlankMarkValueLine>
            </BlankMark>
          ))}
        {warning}
      </EditSongCell>
    </>
  );
};

const SongCellContent = styled.div<{ $sticky: boolean; $isInternalUser: boolean }>`
  color: var(--color-text);
  display: flex;
  flex-direction: column;
  ${(props) => (props.$sticky ? 'position: sticky;' : '')}
  ${(props) => (props.$sticky ? `left: ${getPlatform() === Platform.Mobile ? 0 : SongRowWidth};` : '')}
  padding: ${SongCellPadding};
  /* overcoming Apple's poor CSS support */
  ${getDevice() === Device.iOS ? 'padding-right: 0;' : ''};
  white-space: nowrap;
  /* a long-press opens the cell's menu, not iOS's callout or a text selection (an iPad asking for the desktop site
     reports a desktop platform, so this holds everywhere). Not on the whole cell: WebKit then blocks the caret and
     selection in the text editor's input. */
  -webkit-touch-callout: none;
  user-select: none;
`;

const SongCellSongNameWidth = `${SongRowWidth} - (${SongCellPadding} * 2)`;

// the height of the cell's tag, and of the line a blank's mark sits on
const CellTagHeight = '18px';

// the song, after the cell's tag when it has one and before its verses suffix ('vv. 1–3, 6', issue #4169): the tag is
// always shown whole and the song gives way and ellipsizes. With verses the song and its suffix fill the room after the
// tag, the suffixRoom a long suffix takes two-thirds of (issue #4212), so that room is fixed: the whole width of the
// cell's song rather than the width of what it shows, which no box around the cell's contents has. A cell with blank
// marks keeps the line even when it shows nothing, so the marks' line (BlankMarkValueLine) lies where it does
const SongCellValue = styled.div<{ $hasVerses: boolean; $hasBlankMarks: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  ${(props) => (props.$hasVerses ? 'width' : 'max-width')}: calc(${SongCellSongNameWidth});
  ${(props) => (props.$hasBlankMarks ? `min-height: ${CellTagHeight};` : '')}
`;

const SongCellSongAndVerses = styled.div`
  ${nameWithSuffixRow}
  ${suffixRoom}
  flex: 1;
  align-items: center;
  gap: 8px;
`;

const CellTag = styled.span<{ $outlined: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  height: ${CellTagHeight};
  padding: 0 6px;
  border-radius: 9px;
  border: 1px solid ${(props) => (props.$outlined ? 'var(--color-planner-tag-outline)' : 'transparent')};
  background-color: ${(props) => (props.$outlined ? 'transparent' : 'var(--color-planner-tag-background)')};
  color: ${(props) => (props.$outlined ? 'var(--color-planner-tag-outline-text)' : 'var(--color-planner-tag-text)')};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11px;
  letter-spacing: 0.04em;
`;

const SongCellSongName = styled.div<{ $highlight: boolean; $inherited: boolean }>`
  ${nameBeforeSuffix}
  color: ${(props) => getSongNameColor(props)};
`;

// a song with music ($highlight) in purple, plain text in the text color; an inherited cell's in their faded forms
function getSongNameColor({ $highlight, $inherited }: { $highlight: boolean; $inherited: boolean }): string {
  if ($inherited) {
    return $highlight ? 'var(--color-planner-inherited-song-text)' : 'var(--color-planner-inherited-text)';
  }
  return $highlight ? 'var(--color-date-highlight-text)' : 'var(--color-text)';
}

const SongCellVerses = styled.div`
  ${suffixAfterNameInFillingRow}
  font-size: 0.85em;
  color: var(--color-text-darker);
`;

const EditSongCell = styled.div<{
  $isFixedWidth: boolean;
  $isDoubleHeight: boolean;
  $drawsFocusBorder: boolean; // false for a run, whose selected Sunday's reach the planner outlines
  $disabled?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  height: ${SongRowHeightSingle};
  ${(props) => props.$isFixedWidth && `width: ${SongRowWidth};`}
  /* outline instead of border takes up 0 space */
  outline: 1px solid var(--color-text-lighter);
  height: ${(props) => (props.$isDoubleHeight ? SongRowHeightDouble : SongRowHeightSingle)};
  ${(props) =>
    getPlatform() !== Platform.Mobile &&
    !props.$disabled &&
    `
    border: ${CellHoverOutlineWidth} solid transparent; // avoids text movement on selection
    &:hover {
      background: var(--color-background-selected-item);
      border: ${CellHoverOutlineWidth} solid var(--color-background-selected-item); // avoids white border on hover
      cursor: pointer;
    }
    ${
      props.$drawsFocusBorder
        ? `
    &:focus-within {
      border: ${CellHoverOutlineWidth} solid var(--color-text);
      z-index: 2;
    }`
        : ''
    }
  `}
`;

const CellLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Jost-SemiBold, Arial, sans-serif;
`;

// the width of the cell's border (EditSongCell), which a phone does not draw
const getCellBorderWidth = () => (getPlatform() === Platform.Mobile ? '0px' : CellHoverOutlineWidth);

// A blank's mark (issue #4211): its hover text over its Sunday's whole column, and the dash on the line a song would
// take there. Laid out on the cell, not in its content, which a wide cell keeps in view (sticky) and so would carry the
// mark away from its Sunday; the label and tag lines are mirrored with empty stand-ins so the dash lies where the song
// would. In a cell with a tag every mark leaves the tag's room ahead of its dash, not only the first Sunday's: the
// sticky tag can sit at the start of any of the cell's Sundays, and so never covers a dash. Events on it are the
// cell's: they bubble there, and the column a click selects is read from its position. Dropping the dash and keeping
// the hover is removing BlankDash.
const BlankMark = styled.div<{ $column: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  /* the column's left edge: the cell's padding box starts inside its border, which a phone does not draw */
  left: calc(${(props) => props.$column} * ${SongRowWidth} - ${getCellBorderWidth});
  width: ${SongRowWidth};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* the dash starts where a cell's content would start on that Sunday, inside the border */
  padding: 0 ${SongCellPadding} 0 calc(${getCellBorderWidth} + ${SongCellPadding});
  -webkit-touch-callout: none;
  user-select: none;
`;

// the label line's stand-in: as tall as the cell's label (CellLabel), its text and its glyphs (as tall as a tag)
const BlankMarkLabelLine = styled(CellLabel)<{ $hasText: boolean }>`
  min-height: ${CellTagHeight};
  ${(props) => (props.$hasText ? "&::before { content: '\\00a0'; }" : '')}
`;

const BlankMarkValueLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${CellTagHeight};
`;

// the tag's stand-in, as wide as the tag with no text of its own
const BlankMarkTagSpace = styled(CellTag)`
  visibility: hidden;
  &::before {
    content: attr(data-tag);
  }
`;

const BlankDash = styled.span`
  flex-shrink: 0;
  width: 14px;
  height: 2px;
  border-radius: 1px;
  background-color: var(--color-text-disabled);
`;

// TODO(hewitt): combine these into the same .svg file and set <title> content dynamically
//               problem with setting child textContent attribute is that it changes for all instances
//               problem with wrapping in an outer <svg><title>text</title><Warning/></svg> is the svg does not draw
const SongWarning = styled(SongWarningIcon)`
  position: absolute;
  width: 20px;
  height: 20px;
  right: 5px;
  stroke: var(--color-warning);
`;

const TextWarning = styled(TextWarningIcon)`
  position: absolute;
  width: 20px;
  height: 20px;
  right: 5px;
  stroke: var(--color-warning);
`;
